import { useAppSetting } from "@/service/common.services";
import clientDeepLinks from "@/utils/clientDeepLinks";
import { AppDownLoadAddress } from "@/utils/confiigUtils";
import { isIos, isMac } from "@/utils/parserDevice";
import clsx from "clsx";
import { useTranslation } from "next-i18next";
import WeChatMask from "./WeChatMask";
import { isWeixin } from "@/utils/parserDevice";

import { useCallback, useEffect, useMemo, useState } from "react";
import { RightArrowIcon } from "@/components/RFIcon";
import { helpCenterLocaleHost } from "@/utils/util";
import { useRouter } from "next/router";
import ProdCardView from "./advs/ProdCardView";
import ArrowView from "./advs/ArrowView";
import AboutUs from "./advs/AboutUs";
import ImageView from "next/image";

import { handleDeepLinkOpenApp } from "@/utils/evokeApp";
import useIsZH from "@/hooks/useIsZH";
import { ossFullUrl } from "@/utils/getFullWebCdnUrl";

import { DialogOverlay } from "@/components/ui/dialog";
import CustomDialog from "@/containers/ui/dialog/CustomDialog";
import Script from "next/script";
import log2Sentry from "@/utils/log2Sentry";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";

interface Props {
	deepLink?: string;
	campaign?: string; // 邀请码
	mediaSource?: string; // APP 的分享场景
	channel?: string; // 渠道
}

const timestamp = Date.now();
export default function GetAppContainer({ deepLink, campaign, mediaSource, channel }: Props) {
	const { locale } = useRouter();
	const { t } = useTranslation("common");
	const [ios, setIos] = useState(false);
	const [android, setAndroid] = useState(false);
	const [visible, setVisible] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);

	const { data, isLoading } = useAppSetting(timestamp);
	const isZH = useIsZH();

	const isInChain = useMemo(() => {
		if (isLoading) return 0;
		if (!data) return 1;
		const region = data.find((item: { key: string; value: string }) => item.key === "country");
		if (region.value === "CN") {
			return 1;
		}
		return 2;
	}, [isLoading]);

	const downloadConfigs = {
		android: [
			{
				url: AppDownLoadAddress.GOOGLE_PALY,
				text: t("get-it-on-google-play"),
				isInChain: false,
				needOnelink: true,
			},
			{
				url: AppDownLoadAddress.OFFICIAL,
				text: t("download-official-apk"),
				isInChain: false,
				needOnelink: false,
			},
		],
		ios: [
			{
				url: AppDownLoadAddress.APP_STORE_CHINA_MAINLAND,
				text: t("download-for-mainland-china"),
				isInChain: true,
				needOnelink: true,
			},
			{
				url: AppDownLoadAddress.APP_STORE,
				text: t("download-for-international"),
				isInChain: false,
				needOnelink: true,
			},
		],
	};

	const safeInstallNotice = useCallback(() => {
		if (isInChain === 1) {
			return !!android;
		} else {
			return false;
		}
	}, [isInChain, android]);

	const handleOpenApp = () => {
		if (isWeixin()) {
			return setVisible(true);
		}

		const _deepLink = deepLink || clientDeepLinks.assetPage();

		handleDeepLinkOpenApp(_deepLink, undefined, () => {
			setModalVisible(true);
		});
	};

	useEffect(() => {
		if (isIos() || isMac()) {
			setIos(true);
		} else {
			setAndroid(true);
		}
	}, []);

	const renderAndroidApp = () => {
		if (isInChain === 0) return [];
		if (isInChain === 1) {
			return downloadConfigs.android.reverse();
		}
		return downloadConfigs.android;
	};

	const handleDirect = (url: string, isInChain: boolean, isNeedOnelink: boolean) => {
		if (isNeedOnelink) {
			const onelink = onlineOpen(isInChain);
			if (onelink) {
				const _mediaSource = localStorage.getItem("mediaSource") || mediaSource || "";
				const _campaign = localStorage.getItem("campaign") || campaign || "";
				const _channel = localStorage.getItem("channel") || channel || "";
				window.location.href = onelink;
				log2sensors(SensorsEventIds.appsflyer_onelink, {
					onelink,
					mediaSource: _mediaSource,
					campaign: _campaign,
					channel: _channel,
				});
				return;
			}
		}

		if (isWeixin()) {
			return setVisible(true);
		}

		window.location.href = url;
	};

	const handleOpenNotice = () => {
		const url = `${helpCenterLocaleHost(locale)}/docs/android-apk-installation/`;
		window.location.href = url;
	};

	// 空闲时间加载图片资源
	useEffect(() => {
		const imageUrls = [
			"https://web-cdn.rockflow.tech/h5/intents/intent-modal-rock.png",
			"https://web-cdn.rockflow.tech/h5/intents/intent-modal-bg.png",
		];
		const preloadImages = (imageUrls: string[]) => {
			imageUrls.forEach((url) => {
				const img = new Image();
				img.src = url;
			});
		};
		if ("requestIdleCallback" in window) {
			requestIdleCallback(() => preloadImages(imageUrls));
		} else {
			// Fallback for browsers that do not support requestIdleCallback
			setTimeout(() => preloadImages(imageUrls), 2000);
		}
	}, []);

	function onlineOpen(isInChain: boolean) {
		// props + localStorage 兜底，保证三件套都进 OneLink
		const _mediaSource = localStorage.getItem("mediaSource") || mediaSource || "";
		const _campaign = localStorage.getItem("campaign") || campaign || "";
		const _channel = localStorage.getItem("channel") || channel || "";

		if (!_campaign) return "";

		//Initializing Smart Script arguments
		const oneLinkURL = isInChain ? "https://rockflow.onelink.me/GOzK" : "https://rockflow.onelink.me/P4Wc";

		const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
			oneLinkURL: oneLinkURL,
			afParameters: {
				mediaSource: { defaultValue: "user_share" },
				campaign: { defaultValue: _campaign },
				channel: { defaultValue: _channel },
				afChannel: { defaultValue: _channel },
				afCampaign: { defaultValue: _campaign },
				afSub1: { defaultValue: _mediaSource }, // 分享场景，兼容客户端
				afCustom: [{ paramKey: "af_ss_ui", defaultValue: "true" }],
				deepLinkValue: {
					defaultValue: encodeURIComponent(deepLink || clientDeepLinks.assetPage()),
				},
			},
		});

		if (result && result.clickURL) {
			const url = result.clickURL;
			console.log("onelink url: ", url);
			return url;
		}
	}

	const [imageError, setImageError] = useState(false);

	const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
		if (!imageError) {
			setImageError(true);
			e.currentTarget.src = e.currentTarget.src; // Retry loading the image
		}
	};

	return (
		<>
			<Script src="/third-party/appsflyer.min.js" />
			<div className="max-w-screen-sm text-white bg-[#090415] mx-auto">
				<div className="h-[80vh] relative">
					<ImageView
						width={750}
						height={1364}
						src={ossFullUrl("/intents/intent-rockflow-baby.png")}
						alt=""
						className="w-full absolute left-0 top-[6%]"
						priority
						onError={handleImageError}
					/>
					<div className="relative h-full flex flex-col justify-between">
						<h2
							className={clsx("pt-[6.5vh] whitespace-pre-wrap text-center font-bold text-3xl leading-[1.4]")}
							dangerouslySetInnerHTML={{
								__html: t("start-rock-and-roll-invest-in-the-moment"),
							}}
						></h2>

						<div className="pb-10 px-4.5">
							<a
								className="mx-2 bg-white text-dark flex items-center justify-center rounded-full h-12 text-sm font-semibold"
								style={{
									boxShadow: "0px 0px 30px 0px rgba(136, 106, 255, 0.65)",
								}}
								onClick={handleOpenApp}
							>
								{t("open-rockflow-app")}
							</a>

							<div
								className={clsx("text-center mt-4 font-semibold", isZH ? "text-sm" : "text-xs")}
								dangerouslySetInnerHTML={{ __html: t("open-the-rockflow-app") }}
								onClick={() => setModalVisible(true)}
							/>

							{!deepLink && safeInstallNotice() && (
								<div
									className="flex h-12 items-center justify-center text-[.8125rem] font-bold"
									onClick={handleOpenNotice}
								>
									<span className="underline underline-offset-2">{t("safe-install-link")}</span>
									<RightArrowIcon className="mt-1 ml-2" />
								</div>
							)}
						</div>
					</div>
				</div>

				<div className="relative">
					<div
						className="absolute left-0 top-0 w-full h-full"
						style={{
							background: "#090415",
							filter: "blur(37px)",
						}}
					></div>
					<div
						className="absolute left-0 top-[20vh] w-full h-full"
						style={{
							background: "#090415",
							height: "calc(100% - 20vh)",
						}}
					></div>

					<div className="relative z-10 pb-8">
						<a
							className="flex w-full items-center py-2 pl-5"
							style={{
								background: "linear-gradient(90.48deg, #BF6FFF 0%, #5A5EF0 65.63%, #000000 135.45%)",
							}}
						>
							<ImageView
								src="https://web-cdn.rockflow.tech/h5/activities/stock-rebate/invite/double-icon.png"
								width={10}
								height={13}
								alt="gen-ai"
								className="flex-shrink-0"
							/>
							<span className="ml-2 flex-1 text-xs font-bold tracking-[0.2px] text-white next-welcome-txt zh-letter-space">
								{t("next-gen-ai-investment-experience-on-rf", { ns: "common" })}
							</span>
						</a>
						<ArrowView />
						<ProdCardView />
						<AboutUs />
					</div>
				</div>

				<WeChatMask visible={visible} onClose={() => setVisible(false)} />

				<CustomDialog
					overlay={
						<DialogOverlay className="bg-[url(https://web-cdn.rockflow.tech/h5/intents/intent-modal-bg.png)] bg-cover bg-center bg-transparent dark:bg-transparent" />
					}
					visible={modalVisible}
					onClose={() => setModalVisible(false)}
				>
					<div
						className="relative w-full  rounded-lg bg-[#1C1C1C] text-white 
						bg-[url(https://web-cdn.rockflow.tech/h5/intents/intent-modal-rock.png)] bg-no-repeat bg-[position:center_-1.5rem] bg-[length:100%_auto] h-[22.875rem] flex flex-col justify-end"
						onClick={(e) => e.stopPropagation()}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="11"
							height="11"
							viewBox="0 0 11 11"
							fill="none"
							className="absolute top-0 right-0 mt-4.5 mr-4.5"
							onClick={() => setModalVisible(false)}
						>
							<path d="M1 1L10 10M10 1L1 10" stroke="#AFAFAF" strokeWidth="1.3" />
						</svg>

						<div className="px-5">
							<div className="font-semibold text-xl">{t("download-app-intent")}</div>

							{ios && (
								<>
									{downloadConfigs.ios.map((item, index) => (
										<Channel
											key={index}
											isIOS={true}
											text={item.text}
											onClick={() => handleDirect(item.url, item.isInChain, item.needOnelink)}
										/>
									))}
								</>
							)}

							{android && (
								<>
									{renderAndroidApp().map((item, index) => (
										<Channel
											key={index}
											text={item.text}
											onClick={() => handleDirect(item.url, item.isInChain, item.needOnelink)}
										/>
									))}
								</>
							)}
						</div>
					</div>
				</CustomDialog>
			</div>
		</>
	);
}

function Channel(props: { text: string; onClick: () => void; isIOS?: boolean }) {
	return (
		<div
			className="flex items-center my-[1.875rem]"
			onClick={(e) => {
				e.stopPropagation();
				props.onClick();
			}}
		>
			{props.isIOS ? <IOSIcon /> : <AndroidIcon />}
			<span className="flex-1 text-xs font-semibold px-4 mt-0.5">{props.text}</span>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="12"
				height="13"
				viewBox="0 0 12 13"
				fill="none"
				className="flex-shrink-0"
			>
				<path
					d="M4.2 11.1429L9 6.46031L4.2 1.85714"
					stroke="white"
					strokeWidth="1.5"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</div>
	);
}

function AndroidIcon() {
	return (
		<svg
			className="w-6 h-6 flex-shrink-0"
			viewBox="0 0 1024 1024"
			version="1.1"
			xmlns="http://www.w3.org/2000/svg"
			width="200"
			height="200"
		>
			<path
				d="M247.987001 693.495634c0 60.182684 54.235229 108.978017 109.493763 108.978017l0 108.978017c0 30.066783 25.167193 54.489008 55.258535 54.489008s55.258535-24.422226 55.258535-54.489008l0-107.764376 106.423845 0 0 107.764376c0 30.066783 25.167193 54.489008 55.258535 54.489008 30.088272 0 55.258535-24.422226 55.258535-54.489008L684.938748 802.472628c109.493763 0 109.493763-48.794309 109.493763-108.978017L794.432511 401.526933l-546.44551 0L247.987001 693.495634z"
				fill="#ffffff"
			></path>
			<path
				d="M893.181559 421.051615c-30.088272 0-54.746882 24.373107-54.746882 54.489008l0 217.955011c0 30.066783 24.658609 54.489008 54.746882 54.489008s54.746882-24.422226 54.746882-54.489008L947.928441 475.540623C947.928441 445.423699 923.269831 421.051615 893.181559 421.051615z"
				fill="#ffffff"
			></path>
			<path
				d="M130.818441 421.051615c-30.091342 0-54.746882 24.373107-54.746882 54.489008l0 217.955011c0 30.066783 24.65554 54.489008 54.746882 54.489008 30.091342 0 54.746882-24.422226 54.746882-54.489008L185.565323 475.540623C185.565323 445.423699 160.909783 421.051615 130.818441 421.051615z"
				fill="#ffffff"
			></path>
			<path
				d="M792.641725 333.424882c0-65.288982-34.605146-122.874515-86.371136-155.3563L706.270589 58.060347l-56.352449 0 0 96.650247c-6.640234-1.515516-13.412474-2.673899-20.298302-3.440355-0.373507-7.220449-9.442046-28.196179-29.65746-28.196179L437.597453 123.07406c-20.300348 0-27.402093 21.211091-27.619034 28.314883-5.279237 0.618077-10.487865 1.472538-15.623839 2.533706L394.35458 58.060347 338.002131 58.060347l0 117.779474c-53.780881 32.031531-90.01513 90.775446-90.01513 157.585061l0 11.364839 544.655747 0L792.642748 333.424882zM393.523655 259.799024l-54.6906 0 0-54.6906 54.6906 0L393.523655 259.799024zM704.608739 257.468956l-54.6906 0 0-54.373375 54.6906 0L704.608739 257.468956z"
				fill="#ffffff"
			></path>
		</svg>
	);
}

function IOSIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width="21"
			height="25"
			viewBox="0 0 21 25"
			fill="none"
			className="flex-shrink-0 w-[1.3125rem] h-[1.5625rem]"
		>
			<path
				d="M17.5571 13.2099C17.5451 11.1609 18.4734 9.61672 20.3481 8.47809C19.2996 6.97595 17.7133 6.14977 15.6223 5.99054C13.6425 5.83432 11.4764 7.14419 10.6833 7.14419C9.84506 7.14419 7.92833 6.04462 6.42017 6.04462C3.30773 6.09269 0 8.52616 0 13.4772C0 14.9403 0.267382 16.4515 0.802146 18.0077C1.51717 20.0566 4.09485 25.0768 6.78369 24.9957C8.1897 24.9626 9.18412 23.9983 11.0137 23.9983C12.7893 23.9983 13.7086 24.9957 15.2768 24.9957C17.9897 24.9566 20.321 20.3931 21 18.3382C17.3618 16.6227 17.5571 13.315 17.5571 13.2099Z"
				fill="white"
			/>
			<path
				d="M14.3996 4.04678C15.9228 2.2382 15.7846 0.591845 15.7396 0C14.3936 0.0781116 12.8374 0.916309 11.9511 1.94678C10.9747 3.05236 10.4009 4.41931 10.5241 5.96051C11.9782 6.07167 13.3061 5.3236 14.3996 4.04678Z"
				fill="white"
			/>
		</svg>
	);
}
